import api, { buildQueryString } from './api';
import type {
  ApiResponse,
  DailyReportRow,
  VisitReportRow,
  TrackingGapRow,
  TenantAdminStats,
  ManagerStats,
} from '../types';

export interface ReportFilters {
  date?: string;
  startDate?: string;
  endDate?: string;
  employeeId?: string;
}

export const reportsService = {
  async getTenantDashboardStats(): Promise<TenantAdminStats> {
    const response = await api.get<ApiResponse<TenantAdminStats>>('/api/reports/tenant-dashboard');
    return response.data.data;
  },

  async getManagerDashboardStats(): Promise<ManagerStats> {
    const response = await api.get<ApiResponse<ManagerStats>>('/api/reports/manager-dashboard');
    return response.data.data;
  },

  async getDailyReport(params: ReportFilters): Promise<DailyReportRow[]> {
    const qs = buildQueryString(params);
    const response = await api.get<ApiResponse<DailyReportRow[]>>(`/api/reports/daily${qs}`);
    return response.data.data;
  },

  async getVisitReport(params: ReportFilters): Promise<VisitReportRow[]> {
    const qs = buildQueryString(params);
    const response = await api.get<ApiResponse<VisitReportRow[]>>(`/api/reports/visits${qs}`);
    return response.data.data;
  },

  async getTrackingGapReport(params: ReportFilters): Promise<TrackingGapRow[]> {
    const qs = buildQueryString(params);
    const response = await api.get<ApiResponse<TrackingGapRow[]>>(`/api/reports/tracking-gaps${qs}`);
    return response.data.data;
  },

  async getActiveAlerts(params?: { employeeId?: string }): Promise<TrackingGapRow[]> {
    const qs = buildQueryString(params ?? {});
    const response = await api.get<ApiResponse<TrackingGapRow[]>>(`/api/reports/alerts${qs}`);
    return response.data.data;
  },

  async resolveAlert(id: string): Promise<void> {
    await api.patch(`/api/reports/alerts/${id}/resolve`);
  },

  exportToCSV(data: Record<string, unknown>[], filename: string): void {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((h) => {
        const val = row[h];
        const str = val === null || val === undefined ? '' : String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(','),
    );
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};
